'use client';

import { cn } from '@/lib/utils';
import { Button } from '@/shared/ui/shadcn/button';
import { ArrowDownIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useEffect } from 'react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';

export type ConversationProps = ComponentProps<typeof StickToBottom>;

export const Conversation = ({ className, ...props }: ConversationProps) => (
  <StickToBottom
    className={cn('relative flex-1 overflow-clip', className)}
    initial="smooth"
    resize="smooth"
    role="log"
    {...props}
  />
);

export type ConversationContentProps = ComponentProps<typeof StickToBottom.Content>;

export const ConversationContent = ({ className, ...props }: ConversationContentProps) => (
  <StickToBottom.Content className={cn('flex flex-col gap-4 p-4', className)} {...props} />
);

/** AI Elements' scroll control, positioned above Cortex's floating composer. */
export function ConversationScrollButton() {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  return !isAtBottom ? (
    <Button
      aria-label="Scroll to latest message"
      className="absolute bottom-[calc(var(--dock-height,96px)+16px)] left-1/2 z-20 -translate-x-1/2 rounded-full bg-background shadow-sm"
      onClick={() => void scrollToBottom()}
      size="icon-sm"
      type="button"
      variant="outline"
    >
      <ArrowDownIcon className="size-4" aria-hidden="true" />
    </Button>
  ) : null;
}

/** Brings the thread back to the latest message whenever the physician sends a new one. */
export function ConversationFollowOnSend({ lastUserMessageId }: { lastUserMessageId?: string }) {
  const { scrollToBottom } = useStickToBottomContext();

  useEffect(() => {
    if (lastUserMessageId) {
      void scrollToBottom();
    }
  }, [lastUserMessageId, scrollToBottom]);

  return null;
}
